/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import Header from "../components/header";
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import TopHospitalsIndia from "../components/TopHospitalsIndia";
import TopHospitalsTurkey from "../components/TopHospitalsTurkey";
import { useNavigate } from 'react-router-dom';
import { doctorService, hospitalService, treatmentService } from '../services/api';
import { hospitalNameToSlug } from '../utils/hospitalUtils';
import { treatmentTitleToSlug } from '../utils/treatmentUtils';
import { countries } from '../constants/countries';
import ReCAPTCHA from "react-google-recaptcha";

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

const Hospitals = () => {
  // State for filtering hospital
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '',
    mobile: '',
    email: '',
    preferredLanguage: '',
    country: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcyZ50rAAAAALJS9q7T5m_U115V1UYbM6b20PjA';
  
  // New search functionality from home page
  const navigate = useNavigate();
  const [homeSearchResults, setHomeSearchResults] = useState({ doctors: [], hospitals: [], treatments: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = React.useRef(null);

  // Search ranking utility functions
  const calculateRelevanceScore = (item, query, type) => {
    const lowerQuery = query.toLowerCase().trim();
    let score = 0;
    
    // Primary fields for ranking (starting character matches get highest priority)
    let primaryField = '';
    if (type === 'doctor') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'hospital') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'treatment') {
      primaryField = item.title?.toLowerCase() || '';
    }
    
    // Highest priority: Exact match at start of primary field
    if (primaryField.startsWith(lowerQuery)) {
      score += 1000;
    }
    // High priority: Primary field contains query at word boundary
    else if (primaryField.includes(' ' + lowerQuery) || primaryField.includes('-' + lowerQuery)) {
      score += 800;
    }
    // Medium-high priority: Primary field contains query anywhere
    else if (primaryField.includes(lowerQuery)) {
      score += 600;
    }
    
    // Secondary fields for additional scoring
    const secondaryFields = [];
    if (type === 'doctor') {
      secondaryFields.push(
        item.specialty?.toLowerCase() || '',
        item.hospital?.toLowerCase() || '',
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'hospital') {
      secondaryFields.push(
        item.country?.toLowerCase() || '',
        ...(item.specialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'treatment') {
      secondaryFields.push(
        ...(item.associatedSpecialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || '',
        item.shortDescription?.toLowerCase() || ''
      );
    }
    
    // Check secondary fields for matches
    secondaryFields.forEach(field => {
      if (field.startsWith(lowerQuery)) {
        score += 400;
      } else if (field.includes(' ' + lowerQuery) || field.includes('-' + lowerQuery)) {
        score += 200;
      } else if (field.includes(lowerQuery)) {
        score += 100;
      }
    });
    
    // Bonus for shorter field names (more specific matches)
    if (primaryField.length > 0 && primaryField.includes(lowerQuery)) {
      score += Math.max(0, 100 - primaryField.length);
    }
    
    return score;
  };

  const rankSearchResults = (results, query) => {
    const rankedResults = {
      doctors: [...results.doctors],
      hospitals: [...results.hospitals],
      treatments: [...results.treatments]
    };
    
    // Rank each category
    rankedResults.doctors = rankedResults.doctors
      .map(doctor => ({
        ...doctor,
        relevanceScore: calculateRelevanceScore(doctor, query, 'doctor')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.hospitals = rankedResults.hospitals
      .map(hospital => ({
        ...hospital,
        relevanceScore: calculateRelevanceScore(hospital, query, 'hospital')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.treatments = rankedResults.treatments
      .map(treatment => ({
        ...treatment,
        relevanceScore: calculateRelevanceScore(treatment, query, 'treatment')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    return rankedResults;
  };

  // Function to get ordered sections based on highest relevance scores
  const getOrderedSections = (searchResults) => {
    const sections = [];
    
    // Calculate max score for each category
    const maxScores = {
      doctors: Math.max(...searchResults.doctors.map(d => d.relevanceScore || 0), 0),
      hospitals: Math.max(...searchResults.hospitals.map(h => h.relevanceScore || 0), 0),
      treatments: Math.max(...searchResults.treatments.map(t => t.relevanceScore || 0), 0)
    };
    
    // Create section objects with their max scores
    if (searchResults.doctors.length > 0) {
      sections.push({ type: 'doctors', maxScore: maxScores.doctors, data: searchResults.doctors });
    }
    if (searchResults.hospitals.length > 0) {
      sections.push({ type: 'hospitals', maxScore: maxScores.hospitals, data: searchResults.hospitals });
    }
    if (searchResults.treatments.length > 0) {
      sections.push({ type: 'treatments', maxScore: maxScores.treatments, data: searchResults.treatments });
    }
    
    // Sort by highest max score first
    return sections.sort((a, b) => b.maxScore - a.maxScore);
  };

  // Function to render a search result section
  const renderSearchSection = (section) => {
    const { type, data } = section;
    
    if (type === 'doctors') {
      return (
        <div key="doctors" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Doctors
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((doctor) => (
            <div
              key={`doctor-${doctor.id}`}
              onClick={() => handleResultClick('doctor', doctor)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={doctor.image || '/images/default-doctor.jpg'}
                alt={doctor.name}
                className="w-12 h-12 rounded-full object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{doctor.name}</h4>
                <p className="text-sm text-gray-600">{doctor.specialty}</p>
                <p className="text-xs text-blue-600">{doctor.hospital}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'hospitals') {
      return (
        <div key="hospitals" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Hospitals
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((hospital) => (
            <div
              key={`hospital-${hospital.id}`}
              onClick={() => handleResultClick('hospital', hospital)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={hospital.mainImage || '/images/default-hospital.jpg'}
                alt={hospital.name}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{hospital.name}</h4>
                <p className="text-sm text-gray-600">{hospital.country}</p>
                <p className="text-xs text-blue-600">{hospital.specialities?.join(', ')}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'treatments') {
      return (
        <div key="treatments" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Treatments
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((treatment) => (
            <div
              key={`treatment-${treatment.id || treatment._id}`}
              onClick={() => handleResultClick('treatment', treatment)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={treatment.headerImage || '/images/default-treatment.jpg'}
                alt={treatment.title}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{treatment.title}</h4>
                <p className="text-sm text-gray-600">{treatment.associatedSpecialities?.slice(0, 2)?.join(', ')}</p>
                <p className="text-xs text-blue-600">
                  {treatment.costInfo?.india ? `Starting from $${treatment.costInfo.india.minCost}` : 'Cost info available'}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  // Debounced search function from home page
  const performSearch = React.useCallback(
    debounce(async (query) => {
      if (query.trim().length < 2) {
        setHomeSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        // Search doctors, hospitals, and treatments in parallel with increased limits for better ranking
        const [doctorsResponse, hospitalsResponse, treatmentsResponse] = await Promise.all([
          doctorService.getAll({ search: query, limit: 20 }),
          hospitalService.getAll({ search: query, limit: 20 }),
          treatmentService.getAll({ search: query, limit: 20, status: 'published' })
        ]);

        const rawResults = {
          doctors: doctorsResponse.data?.doctors || [],
          hospitals: hospitalsResponse.data?.hospitals || [],
          treatments: treatmentsResponse.data?.treatments || []
        };

        // Apply ranking system
        const rankedResults = rankSearchResults(rawResults, query);

        setHomeSearchResults(rankedResults);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setHomeSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  // Handle clicking on a search result
  const handleResultClick = (type, item) => {
    setShowResults(false);
    setSearchQuery('');
    
    if (type === 'doctor') {
      navigate(`/doctor-details/${item.id}`);
    } else if (type === 'hospital') {
      navigate(`/hospital/${hospitalNameToSlug(item.name)}`);
    } else if (type === 'treatment') {
      navigate(`/treatments/${item.slug || treatmentTitleToSlug(item.title)}`);
    }
  };

  // Handle search button click
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to a more comprehensive search results page
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  // Close search results when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounced function
  React.useEffect(() => {
    return () => {
      performSearch.cancel();
    };
  }, [performSearch]);

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate reCAPTCHA
    if (!recaptchaValue) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://medical-linkers.onrender.com/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType: 'hospital-inquiry',
          recaptchaToken: recaptchaValue
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
        setFormData({
          name: '',
          countryCode: '',
          mobile: '',
          email: '',
          preferredLanguage: '',
          country: '',
          city: '',
          message: ''
        });
        setRecaptchaValue(null);
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gray-50 py-10 md:py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">World's </span>
              <span className="text-[#1976f2]">Best Hospitals</span>
              <span className="text-gray-900"> For Your Treatment</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get your treatment from the most caring hands on the globe.
              Assured treatment from select premium hospitals without a que.
            </p>

            {/* Search Box */}
            <div className="relative max-w-lg mx-auto mt-8" ref={searchRef}>
              <form onSubmit={handleSearchSubmit}>
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search for Hospitals/Doctors/Specialisation"
                  className="w-full py-3 px-4 rounded-full border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button 
                  type="submit"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-[#1976f2] text-white p-2 rounded-full hover:bg-blue-600 transition-colors"
                >
                  {isSearching ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </form>

              {/* Search Results Dropdown */}
              {showResults && (homeSearchResults.doctors.length > 0 || homeSearchResults.hospitals.length > 0 || homeSearchResults.treatments.length > 0) && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50 max-h-96 overflow-y-auto">
                  {/* Render sections in order of relevance */}
                  {getOrderedSections(homeSearchResults).map(section => renderSearchSection(section))}

                  {/* View All Results */}
                  <div className="p-3 border-t border-gray-100">
                    <button
                      onClick={handleSearchSubmit}
                      className="w-full text-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View all results for "{searchQuery}"
                    </button>
                  </div>
                </div>
              )}

              {/* No Results Message */}
              {showResults && homeSearchResults.doctors.length === 0 && homeSearchResults.hospitals.length === 0 && homeSearchResults.treatments.length === 0 && !isSearching && searchQuery.length >= 2 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50">
                  <div className="p-4 text-center text-gray-500">
                    No results found for "{searchQuery}"
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Main Image Banner */}
        <section className="-mt-6">
          <div className="overflow-hidden">
            <img
              src="/images/hospitalmain-hero.webp"
              alt="Hospital Building"
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        {/* Countries Section */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="text-4xl font-bold text-center">
            <span className="text-[#1976f2]">Countries</span> Available
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-12">
            <div className="flex flex-col items-center">
              <img
                src="/images/image.png"
                alt="India"
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2">India</h3>
              <a
                href="/IndiaMedicalPage"
                className="text-[#1976f2] flex items-center"
              >
                Explore more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>

            <div className="flex flex-col items-center">
              <img
                src="/images/image copy.png"
                alt="Turkey"
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2">Turkey</h3>
              <a
                href="/TurkeyMedicalPage"
                className="text-[#1976f2] flex items-center"
              >
                Explore more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>

            <div className="flex flex-col items-center">
              <img
                src="/images/image copy 8.png"
                alt="Thailand"
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2">Thailand</h3>
              <a
                href="/ThailandMedicalPage"
                className="text-[#1976f2] flex items-center"
              >
                Explore more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>

            <div className="flex flex-col items-center">
              <img
                src="/images/Gyeongbokgung-Palace.jpg"
                alt="South Korea"
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2">South Korea</h3>
              <a
                href="/SouthKoreaMedicalPage"
                className="text-[#1976f2] flex items-center"
              >
                Explore more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* Your Hospital Guide Section */}
        <section className="py-12">
          <div className="container mx-auto">
            <h2 className="text-center text-4xl font-bold mb-16">
              <span className="text-[#1976f2]">Your Hospital</span> Guide
            </h2>

            <div className="max-w-5xl mx-auto">
              <div className="flex flex-wrap">
                <div className="w-full md:w-1/2 mb-10">
                  <div className="flex items-start">
                    <div className="mr-5">
                      <div className="bg-[#1976f2] text-white text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center">
                        1
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Send an inquiry with your reports
                      </h3>
                      <p className="text-gray-600">
                        Share your medical reports with us, and our team will
                        connect you with top hospitals and specialists for
                        expert evaluation.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-1/2 mb-10">
                  <div className="flex items-start">
                    <div className="mr-5">
                      <div className="bg-[#1976f2] text-white text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center">
                        2
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Select from multiple hospital options
                      </h3>
                      <p className="text-gray-600">
                        We provide a list of accredited hospitals with treatment
                        plans, costs, and doctor details, helping you choose the
                        best option.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-1/2 mb-10">
                  <div className="flex items-start">
                    <div className="mr-5">
                      <div className="bg-[#1976f2] text-white text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center">
                        3
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Confirm travel with our travel assistant
                      </h3>
                      <p className="text-gray-600">
                        Our team helps with visa, flights, stay, and transport,
                        ensuring a hassle-free journey for your treatment and
                        recovery.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-1/2 mb-10">
                  <div className="flex items-start">
                    <div className="mr-5">
                      <div className="bg-[#1976f2] text-white text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center">
                        4
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Receive treatment & care
                      </h3>
                      <p className="text-gray-600">
                        Get world-class treatment from expert doctors, with 24/7
                        support to make your hospital stay smooth and
                        comfortable.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-1/2 mb-10">
                  <div className="flex items-start">
                    <div className="mr-5">
                      <div className="bg-[#1976f2] text-white text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center">
                        5
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        We stay in touch for future support
                      </h3>
                      <p className="text-gray-600">
                        Even after treatment, we assist with follow-ups, medical
                        queries, and ongoing support for a stress-free recovery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">Need Help?</h2>
            <p className="text-center text-gray-600 mb-8">
              Fill the form to get a call back from our support team
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Status Message */}
              {submitStatus && (
                <div className={`p-3 rounded font-open-sans ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full p-3 border border-gray-300 rounded"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <select 
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded text-gray-500"
                    required
                  >
                    <option value="">Select Code</option>
                    {countries.map((country) => (
                      <option key={country.code + country.name} value={country.code}>
                        {country.flag} {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    required
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="w-full p-3 border border-gray-300 rounded"
                />
              </div>

              <div>
                <input
                  type="text"
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                  placeholder="Your Preferred Language"
                  className="w-full p-3 border border-gray-300 rounded"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe Your Questions"
                  required
                  rows="4"
                  className="w-full p-3 border border-gray-300 rounded"
                ></textarea>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-center">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={() => setRecaptchaValue(null)}
                    theme="light"
                  />
                </div>
                {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                  <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                )}
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#1976f2] text-white px-8 py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Internationally Recognized Accreditations Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold">
              <span className="text-[#1976f2]">Internationally Recognized</span>{" "}
              Accreditations
            </h2>
            <p className="text-gray-600 mt-4">
              All hospitals & clinics have internationally recognized
              accreditations
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8 mt-12">
            <img
              src="/images/image copy 2.png"
              alt="TUV Certification"
              className="h-24"
            />
            <img
              src="/images/image copy 3.png"
              alt="ISO Certification"
              className="h-24"
            />
            <img
              src="/images/image copy 4.png"
              alt="JCI Certification"
              className="h-24"
            />
            <img
              src="/images/image copy 5.png"
              alt="Temos Travel"
              className="h-24"
            />
            <img
              src="/images/image copy 6.png"
              alt="Temos Medical"
              className="h-24"
            />
            <img
              src="/images/image copy 7.png"
              alt="NABH Certification"
              className="h-24"
            />
          </div>
        </section>

        {/* p Hospitals Section */}
        <TopHospitalsIndia />

        <TopHospitalsTurkey />
        <section className="container mx-auto px-4 py-16">
          <div className="flex flex-col md:flex-row items-center bg-white rounded-lg overflow-hidden shadow-lg">
            <div className="md:w-1/2 p-8">
              <h2 className="text-3xl font-bold mb-2">
                Get treated at preferred hospital
              </h2>
              <p className="text-gray-600 mb-6">
                Start your treatment journey now
              </p>

              {/* Status Message */}
              {submitStatus && (
                <div className={`p-3 rounded-lg mb-4 font-open-sans ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <select 
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded text-gray-500"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code + country.name} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Mobile Number"
                      required
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div>
                  <input
                    type="text"
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleChange}
                    placeholder="Your Preferred Language"
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Country"
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City"
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe Your Questions"
                    required
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded"
                  ></textarea>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-center">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaValue(null)}
                      theme="light"
                    />
                  </div>
                  {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                    <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#1976f2] text-white px-8 py-3 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>

            <div className="md:w-1/2">
              <img
                src="/images/image copy 22.png"
                alt="Medical Professional"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingChatButton />
    </div>
  );
};

export default Hospitals;
